import { useQuery } from '@tanstack/react-query';

import { axiosPrivate } from 'app/utils';

export interface OrganisationAddress {
  name?: string;
  street?: string;
  city?: string;
  postcode?: string;
}

/** The contact person recorded on the organisation. Absent when none is recorded. */
export interface OrganisationPrimaryUser {
  firstName?: string;
  lastName?: string;
  email?: string;
  position?: string;
  telephone?: string;
}

export interface Organisation {
  organisationId: string;
  name: string;
  address?: OrganisationAddress;
  primaryUser?: OrganisationPrimaryUser;
}

export const organisationQueryKey = (organisationId: string): string[] => [
  'organisation',
  organisationId,
];

function getOrganisation(organisationId: string) {
  return axiosPrivate.get<Organisation>(`/v3/pharmacist/organisations/${organisationId}`);
}

export interface UseOrganisationResult {
  organisation: Organisation | null;
  isLoading: boolean;
  isError: boolean;
}

/**
 * The organisation record, used to preset fields the pharmacy has already told us about rather
 * than as content in its own right. A failure is deliberately silent (`silenceErrors` keeps the
 * global query error toast away, no retry): the user simply types the values themselves.
 */
export function useOrganisation(organisationId: string): UseOrganisationResult {
  const {
    data,
    isInitialLoading: isLoading,
    isError,
  } = useQuery({
    queryKey: organisationQueryKey(organisationId),
    queryFn: () => getOrganisation(organisationId),
    enabled: !!organisationId,
    retry: false,
    meta: { silenceErrors: true },
  });

  return {
    organisation: data?.data ?? null,
    isLoading,
    isError,
  };
}
